import { create } from "zustand";
import { CardProps } from "../types/Card";
import useStoreRoomSession from "./useStoreRoomSession";

type StateStoreRoomScene = {
    cards: Record<string, Array<CardProps>>,
    hoveredCardId: string | null,
};

type StoreRoomSceneActions = {
    position: (playerId: string, cardId: string) => number,
    has: (playerId: string, cardId: string) => boolean,
    add: (playerId: string, position: number, card: CardProps) => void,
    remove: (playerId: string, position: number) => void,
    replace: (playerId: string, cardId: string, newCard: CardProps) => void,
    refresh: (playerId: string, newCards: Array<CardProps>) => void,
    setHoveredCardId: (cardId: string | null) => void,
    reset: () => void,
};

const useStoreRoomScene = create<StateStoreRoomScene & StoreRoomSceneActions>((set, get) => ({
    cards: {},
    hoveredCardId: null,

    position: (playerId: string, cardId: string): number => {
        const cards = get().cards[playerId] || [];
        return cards.findIndex(card => card.id === cardId);
    },

    has: (playerId: string, cardId: string): boolean => {
        const cards = get().cards[playerId] || [];
        return cards.some(card => card.id === cardId);
    },

    add: (playerId: string, position: number, card: CardProps) => {
        set(state => {
            const playerCards = [...(state.cards[playerId] || [])];

            if(playerCards.some(({ id }) => id === card.id)) {
                return state;
            }

            playerCards.splice(position, 0, card);
            
            return {
                cards: {
                    ...state.cards,
                    [playerId]: playerCards
                }
            };
        }, false);
    },

    remove: (playerId: string, position: number) => {
        set(state => {
            const playerCards = [...(state.cards[playerId] || [])];
            playerCards.splice(position, 1);
            return {
                cards: {
                    ...state.cards,
                    [playerId]: playerCards
                }
            };
        }, false);
    },

    replace: (playerId: string, cardId: string, newCard: CardProps) => {
        set(state => {
            const playerCards = [...(state.cards[playerId] || [])];
            const cardIndex = playerCards.findIndex(card => card.id === cardId);

            if(cardIndex === -1) {
                throw new Error(`Card ${cardId} not found for player ${playerId}`);
            }

            playerCards[cardIndex] = newCard;
            return {
                cards: {
                    ...state.cards,
                    [playerId]: playerCards
                }
            };
        }, false);
    },

    refresh: (playerId: string, newCards: Array<CardProps>) => {
        set(state => ({
            cards: {
                ...state.cards,
                [playerId]: [...newCards]
            }
        }), false);
    },

    setHoveredCardId: (cardId: string | null) => set(() => ({ hoveredCardId: cardId }), false),

    reset: () => set(() => ({ cards: {}, hoveredCardId: null }), false),
}));

useStoreRoomSession.subscribe(
    (state) => state.roomId,
    () => {
        useStoreRoomScene.getState().reset();
    }
);

export default useStoreRoomScene;
